/**
 * Utility functions for Tenant Landing Page V2
 */

import { WorkingHourInfo } from "@/types/tenant-landing-v2";

/**
 * Converts minutes from midnight to HH:MM format
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
}

/**
 * Gets the day name in Portuguese
 */
export function getDayName(dayOfWeek: number): string {
  const days = [
    "Domingo",
    "Segunda-feira",
    "Terça-feira",
    "Quarta-feira",
    "Quinta-feira",
    "Sexta-feira",
    "Sábado",
  ];
  return days[dayOfWeek] || "Desconhecido";
}

/**
 * Gets the short day name in Portuguese
 */
export function getShortDayName(dayOfWeek: number): string {
  const days = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  return days[dayOfWeek] || "";
}

/**
 * Groups working hours by day
 */
export function groupWorkingHoursByDay(
  workingHours: WorkingHourInfo[]
): Map<number, WorkingHourInfo[]> {
  const grouped = new Map<number, WorkingHourInfo[]>();

  for (let i = 0; i < 7; i++) {
    grouped.set(i, []);
  }

  workingHours
    .filter((wh) => wh.isActive)
    .forEach((wh) => {
      const dayHours = grouped.get(wh.dayOfWeek) || [];
      dayHours.push(wh);
      grouped.set(wh.dayOfWeek, dayHours);
    });

  return grouped;
}

/**
 * Formats working hours for display
 */
export function formatWorkingHours(hours: WorkingHourInfo[]): string {
  if (hours.length === 0) return "Fechado";

  const formatted = hours
    .sort((a, b) => a.startMin - b.startMin)
    .map((h) => `${minutesToTime(h.startMin)} - ${minutesToTime(h.endMin)}`)
    .join(", ");

  return formatted;
}

/**
 * Checks if tenant is currently open
 */
export function isCurrentlyOpen(workingHours: WorkingHourInfo[]): boolean {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const todayHours = workingHours.filter(
    (wh) => wh.dayOfWeek === dayOfWeek && wh.isActive
  );

  return todayHours.some(
    (wh) => currentMinutes >= wh.startMin && currentMinutes <= wh.endMin
  );
}

/**
 * Gets next opening time
 */
export function getNextOpeningTime(
  workingHours: WorkingHourInfo[]
): string | null {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  // Check today's remaining hours
  const todayHours = workingHours
    .filter((wh) => wh.dayOfWeek === dayOfWeek && wh.isActive)
    .sort((a, b) => a.startMin - b.startMin);

  for (const wh of todayHours) {
    if (currentMinutes < wh.startMin) {
      return `Hoje às ${minutesToTime(wh.startMin)}`;
    }
  }

  // Check next 7 days
  for (let i = 1; i <= 7; i++) {
    const nextDay = (dayOfWeek + i) % 7;
    const nextDayHours = workingHours
      .filter((wh) => wh.dayOfWeek === nextDay && wh.isActive)
      .sort((a, b) => a.startMin - b.startMin);

    if (nextDayHours.length > 0) {
      const dayName = i === 1 ? "Amanhã" : getDayName(nextDay);
      return `${dayName} às ${minutesToTime(nextDayHours[0].startMin)}`;
    }
  }

  return null;
}

/**
 * Debounce function for performance
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function for scroll events
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Formats price for display
 */
export function formatCurrency(
  priceInCents: number,
  variablePricing: boolean = false
): string {
  const formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const value = formatter.format(priceInCents / 100);

  return variablePricing ? `A partir de ${value}` : value;
}

/**
 * Formats duration in minutes to readable format
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}min`;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}min`;
}

/**
 * Gets contrast color for text (black or white) based on background
 */
export function getContrastColor(hexColor: string): string {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? "#000000" : "#FFFFFF";
}

/**
 * Creates a staggered animation delay
 */
export function getStaggerDelay(index: number, baseDelay: number = 50): number {
  return index * baseDelay;
}

/**
 * Generates a skeleton loader count based on viewport
 */
export function getSkeletonCount(): number {
  if (typeof window === "undefined") return 6;

  const width = window.innerWidth;

  if (width < 640) return 2; // mobile
  if (width < 1024) return 4; // tablet
  return 6; // desktop
}

/**
 * Formats relative time (e.g., "2 dias atrás")
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - new Date(date).getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInSeconds < 60) return "Agora mesmo";
  if (diffInMinutes < 60)
    return `${diffInMinutes} ${
      diffInMinutes === 1 ? "minuto" : "minutos"
    } atrás`;
  if (diffInHours < 24)
    return `${diffInHours} ${diffInHours === 1 ? "hora" : "horas"} atrás`;
  if (diffInDays < 30)
    return `${diffInDays} ${diffInDays === 1 ? "dia" : "dias"} atrás`;
  if (diffInMonths < 12)
    return `${diffInMonths} ${diffInMonths === 1 ? "mês" : "meses"} atrás`;
  return `${diffInYears} ${diffInYears === 1 ? "ano" : "anos"} atrás`;
}

/**
 * Truncates text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

/**
 * Validates if image URL is accessible
 */
export async function isImageAccessible(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: "HEAD" });
    return response.ok;
  } catch {
    return false;
  }
}
